'use client';
import React, { useContext, useEffect } from 'react';
import { useBenzingaEdge } from '@benzinga/edge';
import { SessionContext } from '@benzinga/session-context';
import { TrackingManager } from '@benzinga/tracking-manager';

export interface EdgeRowsGateState {
  /** True when the viewer lacks Edge — caller should blur rows past `visibleRowCount`. */
  isGated: boolean;
  /** How many rows the caller should render un-blurred. */
  visibleRowCount: number;
}

/**
 * For transaction-list tables shown on BZFE-#15156 quote tabs (insider filings,
 * gov trades, analyst ratings, etc.), gate any rows past the first N for
 * non-Edge viewers. Caller is responsible for applying a `blur-*` class to the
 * gated rows and rendering `EdgeGateCTAOverlay` inside a `position: relative`
 * wrapper.
 */
export const useEdgeRowsGate = (totalRows: number, visibleRows = 2): EdgeRowsGateState => {
  const { hasEdgeSubscription } = useBenzingaEdge();
  // Fail closed: gate until we positively know the viewer has Edge. Leaving the
  // gate open while `isEdgeSubscriptionLoading` was true briefly exposed every
  // premium row to non-Edge viewers before gating resolved, so default to gated.
  if (hasEdgeSubscription) {
    return { isGated: false, visibleRowCount: totalRows };
  }
  return { isGated: true, visibleRowCount: Math.min(visibleRows, totalRows) };
};

/**
 * Fires a single Segment `paywall_event` / 'view' the first time the gate
 * surface mounts. Mirrors the PermissionOverlay pattern on /edge/dashboard so
 * the new quote-page gates are visible in the same analytics stream.
 *
 * Callers should only render the component invoking this hook when the gate
 * is actually shown (i.e., the user lacks Edge), so the event is naturally
 * scoped to non-Edge impressions.
 */
// Paywall impressions already reported in this page session, so a gate that
// remounts (e.g. a financials period toggle or unusual-options filter
// re-rendering the overlay) doesn't re-fire. Keyed by paywall id + pathname so
// a genuine navigation to another quote page still counts once.
const reportedGateImpressions = new Set<string>();

export const useGateImpressionTracking = (paywallId: string, placement: string): void => {
  const session = useContext(SessionContext);
  const { hasEdgeSubscription, isEdgeSubscriptionLoading } = useBenzingaEdge();

  useEffect(() => {
    // Report only once Edge status has resolved, and only for non-Edge viewers.
    // The gate fails closed while loading, so firing during that window counts
    // Edge subscribers as paywall impressions and inflates the metric.
    if (isEdgeSubscriptionLoading || hasEdgeSubscription) return;

    const key = `${paywallId}::${typeof window === 'undefined' ? '' : window.location.pathname}`;
    if (reportedGateImpressions.has(key)) return;
    reportedGateImpressions.add(key);

    session.getManager(TrackingManager).trackPaywallEvent('view', {
      paywall_id: paywallId,
      paywall_type: 'soft',
      placement,
      utmParams: { adType: 'paywall', campaign: paywallId },
    });
  }, [hasEdgeSubscription, isEdgeSubscriptionLoading, paywallId, placement, session]);
};

interface EdgeGateCTAOverlayProps {
  /** Identifier carried into the checkout URL's utm `ad` param for tracking. */
  type: string;
}

/**
 * Absolute-positioned compact Edge upsell card. Drop inside a parent element
 * that has `position: relative`. The card has no fixed height so it never
 * gets clipped by the parent's bounds — keeps a consistent compact footprint
 * regardless of which gated table it sits over.
 */
export const EdgeGateCTAOverlay: React.FC<EdgeGateCTAOverlayProps> = ({ type }) => {
  useGateImpressionTracking(`quote-${type}-paywall`, `quote-${type}`);
  const url = `https://www.benzinga.com/premium/ideas/benzinga-edge-checkout/?adType=paywall&ad=${type}`;
  return (
    <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center px-4">
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="pointer-events-auto inline-flex flex-col items-center gap-3 rounded-md bg-blue-500 px-6 py-4 text-white shadow-lg hover:bg-blue-600 transition-colors"
      >
        <span className="text-sm font-semibold uppercase tracking-wide">More with Benzinga Edge</span>
        <span className="inline-flex items-center gap-2 rounded bg-white px-4 py-1.5 text-xs font-bold uppercase tracking-wide text-blue-600">
          <span>Get Access</span>
          <span aria-hidden>→</span>
        </span>
      </a>
    </div>
  );
};
